/**
 * Namespaced localStorage utilities.
 *
 * Keys are stored in `namespace:key` format to avoid collisions between apps.
 * All methods swallow errors (logging to Sentry via reporter.error) so that
 * localStorage failures never crash the calling code.
 *
 * Note: purgeNamespaces reads keys directly from window.localStorage (since
 * StorageManager has no keys() method) but deletes through localStorageManager.
 */
import {localStorageManager} from 'common/contexts/storage-manager';
import {reporter} from 'common/report/error-reporting';

export class NamespacedLocalStorage {
  constructor(private namespace: string) {}

  getItem(key: string): string | null {
    try {
      return localStorageManager.getItem(`${this.namespace}:${key}`);
    } catch (err) {
      reporter.error(err, {info: `namespacedGet failed for ${this.namespace}:${key}`});
      return null;
    }
  }

  setItem(key: string, value: string): void {
    try {
      localStorageManager.setItem(`${this.namespace}:${key}`, value);
    } catch (err) {
      reporter.error(err, {info: `namespacedSet failed for ${this.namespace}:${key}`});
    }
  }

  getObject<T = any>(key: string): T | null {
    const raw = this.getItem(key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch (err) {
      reporter.error(err, {info: `namespacedGetObject parse failed for ${this.namespace}:${key}`});
      return null;
    }
  }

  setObject(key: string, value: unknown): void {
    try {
      this.setItem(key, JSON.stringify(value));
    } catch (err) {
      reporter.error(err, {info: `namespacedSetObject stringify failed for ${this.namespace}:${key}`});
    }
  }

  removeItem(key: string): void {
    try {
      localStorageManager.removeItem(`${this.namespace}:${key}`);
    } catch (err) {
      reporter.error(err, {info: `namespacedRemove failed for ${this.namespace}:${key}`});
    }
  }
}

export function purgeNamespaces(namespaces: string[]): void {
  try {
    // eslint-disable-next-line no-restricted-properties -- need to iterate keys; StorageManager has no keys() method
    for (const key of Object.keys(window.localStorage)) {
      for (const ns of namespaces) {
        if (key.startsWith(`${ns}:`)) {
          localStorageManager.removeItem(key);
        }
      }
    }
  } catch (err) {
    reporter.error(err, {info: `purgeNamespaces failed for [${namespaces.join(`, `)}]`});
  }
}
